/** Template browser endpoints (CONTRACT §2). Read-only catalog of all 196 formats. */
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { api } from "./client";
import type {
  TemplateDetail,
  TemplateFacets,
  TemplateListResult,
  TemplateQuery,
} from "./types";

function toParams(q: TemplateQuery): Record<string, string | number | boolean> {
  const params: Record<string, string | number | boolean> = {};
  if (q.q) params.q = q.q;
  if (q.family) params.family = q.family;
  if (q.output_format) params.output_format = q.output_format;
  if (q.recoverability) params.recoverability = q.recoverability;
  if (q.test_status) params.test_status = q.test_status;
  if (q.kind) params.kind = q.kind;
  if (q.in_mdb !== undefined) params.in_mdb = q.in_mdb;
  params.limit = q.limit ?? 50;
  params.offset = q.offset ?? 0;
  return params;
}

export function useTemplates(query: TemplateQuery, enabled = true) {
  return useQuery({
    queryKey: ["templates", query],
    queryFn: async (): Promise<TemplateListResult> => {
      const { data } = await api.get<TemplateListResult>("/templates", {
        params: toParams(query),
      });
      return data;
    },
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useTemplateFacets(enabled = true) {
  return useQuery({
    queryKey: ["template-facets"],
    queryFn: async (): Promise<TemplateFacets> => {
      const { data } = await api.get<TemplateFacets>("/templates/facets");
      return data;
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useTemplateDetail(classOrName: string | undefined) {
  return useQuery({
    queryKey: ["template", classOrName],
    queryFn: async (): Promise<TemplateDetail> => {
      const { data } = await api.get<TemplateDetail>(
        `/templates/${encodeURIComponent(classOrName as string)}`,
      );
      return data;
    },
    enabled: !!classOrName,
  });
}
